import { useCallback, useState, useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./ExpenseCategories.module.css";
import { useNavigate } from "react-router";
import axios from "axios";
import { observer } from "mobx-react";
import config from "../../config/config";
import store from "../../Store/Store";
import { Pagination } from "antd";
import { jwtDecode } from "jwt-decode";
import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
} from "@aws-sdk/client-cognito-identity-provider";

const ExpenseCategories = observer(({ className = "" }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [refresh, setRefresh] = useState(true);
  const [hoveredItem, setHoveredItem] = useState({
    software: null,
    index: null,
  });
  const [categoryUpdated, setCategoryUpdated] = useState(false);
  const [data, setData] = useState([]);

  const isTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token);

      if (decoded && decoded.exp) {
        const currentTime = Math.floor(Date.now() / 1000); // Get current time in seconds
        return decoded.exp < currentTime;
      }
    } catch (error) {
      console.log("Error decoding token:", error);
      return true; // Treat decoding failures as expired tokens
    }
    return true;
  };

  const getNewTokens = async (authData) => {
    if (!authData?.RefreshToken) {
      localStorage.removeItem("authToken");
      // navigate("/login");
      return;
    }

    try {
      const client = new CognitoIdentityProviderClient({
        region: "ap-south-1",
      });

      const params = {
        ClientId: config.Client_Id,
        AuthFlow: "REFRESH_TOKEN_AUTH",
        AuthParameters: {
          REFRESH_TOKEN: authData?.RefreshToken,
        },
      };

      const command = new InitiateAuthCommand(params);
      const refreshResponse = await client.send(command);

      if (refreshResponse.AuthenticationResult) {
        const newAuthToken = {
          AccessToken: refreshResponse.AuthenticationResult.AccessToken,
          RefreshToken: authData?.RefreshToken, // Keep old refresh token
          IdToken: refreshResponse.AuthenticationResult.IdToken,
        };

        localStorage.setItem("authToken", JSON.stringify(newAuthToken));
        setAuth(newAuthToken); // Update state
        if (store.refresh) {
          store.setRefresh(false);
        } else {
          store.setRefresh(true);
        }
        return newAuthToken;
      } else {
        localStorage.removeItem("authToken");
        // navigate("/login");
      }
    } catch (error) {
      console.log("Error refreshing tokens:", error);
      localStorage.removeItem("authToken");
      // navigate("/login");
    }
  };

  const getCategories = async () => {
    try {
      let tokens = localStorage.getItem("authToken");
      let parsedTokens = JSON.parse(tokens);
      const Obj = {
        ledger_label: store.Company_Id,
      };
      const expired = isTokenExpired(parsedTokens.IdToken);
      if (expired) {
        parsedTokens = getNewTokens(parsedTokens);
      }
      let payload = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${config.node_api_endpoint}/cupi/admin/get_categories`,
        headers: {
          aws_cognito_id_token: parsedTokens.IdToken,
          aws_cognito_access_token: parsedTokens.AccessToken,
          "Content-Type": "application/json",
        },
        data: Obj,
      };
      console.log("OBJ", payload);
      axios
        .request(payload)
        .then((res) => {
          console.log("res.data.expenseCategories", res.data.data);
          setData(res?.data?.data);
        })
        .catch((err) => {
          console.log("Error %o", err);
        });
    } catch (err) {
      console.log("error %o", err);
    }
  };

  useEffect(() => {
    console.log("setData", data);
  }, []);

  useEffect(() => {
    getCategories();
  }, [store.refresh]);

  const setCategories = async () => {
    try {
      let tokens = localStorage.getItem("authToken");
      let parsedTokens = JSON.parse(tokens);
      const Obj = {
        ledger_label: store.Company_Id,
        expenseCategories: data,
      };

      const expired = isTokenExpired(parsedTokens.IdToken);
      if (expired) {
        parsedTokens = getNewTokens(parsedTokens);
      }
      let payload = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${config.node_api_endpoint}/cupi/admin/set_categories`,
        headers: {
          aws_cognito_id_token: parsedTokens.IdToken,
          aws_cognito_access_token: parsedTokens.AccessToken,
          "Content-Type": "application/json",
        },
        data: Obj,
      };
      console.log("OBJ", payload);
      axios
        .request(payload)
        .then(async (res) => {
          console.log(res.data);
          setCategoryUpdated(true);
          await new Promise((resolve) => setTimeout(resolve, 3000));
          setCategoryUpdated(false);
        })
        .catch((err) => {
          console.log("Error %o", err);
        });
    } catch (err) {
      console.log("error %o", err);
    }
  };

  const startIndex = (currentPage - 1) * pageSize;
  let currentPageData = Array.isArray(data)
    ? data.slice(startIndex, startIndex + pageSize)
    : [];

  const handlePaginationChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  // Dynamic DetailPopup component
  const DetailPopup = ({ data }) => {
    if (!data) return null;

    return (
      <div className={styles.detailPopup}>
        {Object.entries(data).map(
          ([key, value]) =>
            typeof value !== "object" &&
            value !== null && (
              <p key={key}>
                <strong>{formatKey(key)}:</strong> {value.toString()}
              </p>
            ),
        )}
      </div>
    );
  };

  // Helper function to format keys
  const formatKey = (key) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .trim()
      .replace(/^./, (str) => str.toUpperCase());
  };

  return (
    <div className={styles.pageContainer + " " + className}>
      <div className={styles.frameParent}>
        <div className={styles.frameGroup}>
          <div className={styles.feeStatementDataTable}>
            <div className={styles.top}>
              <div className={styles.filterWrapper}>
                <div className={styles.filter}>
                  <div className={styles.containertext}>
                    Expense Categories List
                  </div>
                </div>
                {categoryUpdated && (
                  <div className={styles.reLoadTablepopup}>
                    <div className={styles.reLoadcontainer}>
                      Expense categories has been updated.
                    </div>
                  </div>
                )}
                <div className={styles.filter}>
                  {!store.isUserViewAdmin && !store.isUserGlobalViewAdmin ? (
                    <button
                      className={styles.reLoadTable}
                      onClick={async () => {
                        //DONT ALLOW VIEW ADMIN
                        setCategories();
                        if (refresh) {
                          setRefresh(false);
                          return;
                        }
                        setRefresh(true);
                      }}
                    >
                      <div className={styles.reLoadTabletext}>Apply</div>
                    </button>
                  ) : (
                    <button className={styles.reLoadTableinactive}>
                      <div className={styles.reLoadTabletext}>Apply</div>
                    </button>
                  )}
                </div>
              </div>
              <div className={styles.topChild} />
            </div>
            <table className={styles.table}>
              <thead className={styles.tableHead}>
                <tr className={styles.tableHeaderRow}>
                  <th className={styles.tableHeaderRowCell}>
                    <div className={styles.head1}>NAME</div>
                  </th>
                  <th className={styles.tableHeaderRowCell}>
                    <div className={styles.head}>DESCRIPTION</div>
                  </th>
                  <th className={styles.tableHeaderRowCell1}>
                    <div className={styles.head}>ACTION</div>
                  </th>
                  <th className={styles.tableHeaderRowCell}></th>
                </tr>
              </thead>
              <tbody className={styles.tableHead}>
                {currentPageData?.map((detail, index) => {
                  // Get software keys dynamically, excluding non-object properties
                  const softwareKeys = Object.keys(detail).filter(
                    (key) =>
                      typeof detail[key] === "object" &&
                      detail[key] !== null &&
                      key !== "parentCategoryId",
                  );

                  return (
                    <tr key={index} className={styles.tableBodyrow}>
                      <td className={styles.tableBodydata2}>
                        <div className={styles.datacontainerContainer}>
                          {detail.categoryName}
                        </div>
                      </td>
                      <td className={styles.tableBodydata}>
                        <div className={styles.datacontainerContainer1}>
                          {detail.description}
                        </div>
                      </td>
                      <td className={styles.tableBodydata1}>
                        <div className={styles.button}>
                          {detail.categoryName != "Uncategorized" &&
                            detail.categoryName != "Other" &&
                            (!store.isUserViewAdmin &&
                            !store.isUserGlobalViewAdmin ? (
                              <button
                                className={styles.button1}
                                onClick={() => {
                                  setData((prevData) =>
                                    prevData.map((item) =>
                                      item.categoryId === detail.categoryId
                                        ? { ...item, isActive: !item.isActive }
                                        : item,
                                    ),
                                  );
                                }}
                              >
                                {detail.isActive ? (
                                  <img
                                    alt=""
                                    src="/Track(Active).svg"
                                    className={styles.btn_img}
                                  />
                                ) : (
                                  <img
                                    alt=""
                                    src="/Track.svg"
                                    className={styles.btn_img}
                                  />
                                )}
                              </button>
                            ) : (
                              <button className={styles.button22}>
                                {detail.isActive ? (
                                  <img
                                    alt=""
                                    src="/Track(Active).svg"
                                    className={styles.btn_img22}
                                  />
                                ) : (
                                  <img
                                    alt=""
                                    src="/Track.svg"
                                    className={styles.btn_img22}
                                  />
                                )}
                              </button>
                            ))}
                        </div>
                      </td>
                      <td className={styles.softwareInfo}>
                        <div className={styles.softwareContainer}>
                          {softwareKeys.map((software) => (
                            <div
                              key={software}
                              className={styles.softwareItem}
                              onMouseEnter={() =>
                                setHoveredItem({ software, index })
                              }
                              onMouseLeave={() =>
                                setHoveredItem({ software: null, index: null })
                              }
                            >
                              {software.toUpperCase()}
                              {hoveredItem.software === software &&
                                hoveredItem.index === index && (
                                  <div className={styles.hoverDetails}>
                                    <DetailPopup data={detail[software]} />
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className={styles.bottom}>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={data?.length}
                onChange={handlePaginationChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

ExpenseCategories.propTypes = {
  className: PropTypes.string,
};

export default ExpenseCategories;
